import type { ReactElement, ReactNode } from 'react';
import { Trans, type UseTranslationResponse } from 'react-i18next';
import { ApproachingPromotionsBanner } from '@/components/approaching-promotions-banner';
import {
    commerceFeedbackContentClassName,
    type CommerceFeedbackPresentation,
} from '@/components/toast/commerce-notification-styles';
import { cn } from '@/lib/utils';
import {
    resolveAddAllToCartToastUi,
    type AddAllToCartHeaderKey,
    type AddAllToCartSecondaryKey,
} from '@/lib/add-all-to-cart-toast-ui';
import type { ApproachingPromotion, EditorialPromotion } from '@/types/scapi';
import { CartEditorialPromotions } from '../cart/cart-editorial-promotions';
import { SvgCartFeedback } from '../icons-eataly';

type ProductTFunction = UseTranslationResponse<'product', undefined>['t'];
type CartTFunction = UseTranslationResponse<'cart', undefined>['t'];
type AccountTFunction = UseTranslationResponse<'account', undefined>['t'];

export type WishlistSavedToastOptions = {
    productName: string;
    listName: string;
    alreadyInList?: boolean;
};

export type AddedToCartToastOptions = {
    productName?: string;
    quantity?: number;
    approachingPromotions?: ApproachingPromotion[];
    editorialPromotions?: EditorialPromotion[];
    messageKey?: 'addedToCart' | 'updatedCart' | 'addedAllToCartForWishlist' | 'addedAllToCartForRecipe';
    addedItemCount?: number;
    unavailableItemCount?: number;
    isBasketQuotaExceeded?: boolean;
};

export type WishlistAddToCartToastOptions = {
    addedItemCount: number;
    unavailableItemCount: number;
    approachingPromotions?: ApproachingPromotion[];
    editorialPromotions?: EditorialPromotion[];
    isBasketQuotaExceeded: boolean;
};
export type RecipeAddedToCartToastOptions = {
    addedItemCount: number;
    unavailableItemCount: number;
    approachingPromotions?: ApproachingPromotion[];
    editorialPromotions?: EditorialPromotion[];
    isBasketQuotaExceeded: boolean;
};

export type RemovedFromCartToastOptions = {
    productName: string;
    messageKey?: 'removedFromCart' | 'updatedCart';
};

export type MinOrderFeedbackToastOptions = {
    formattedAmount: string;
};

export type AddedToCartToastContentProps = AddedToCartToastOptions & {
    t: ProductTFunction;
    tAccount: AccountTFunction;
    tCart: CartTFunction;
    currency: string;
    language: string;
    presentation?: CommerceFeedbackPresentation;
};

type CommerceFeedbackContentProps = {
    presentation?: CommerceFeedbackPresentation;
};

export function WishlistSavedToastContent({ productName, listName, alreadyInList }: WishlistSavedToastOptions) {
    if (alreadyInList) {
        return (
            <Trans
                i18nKey="product:wishlistSheet.confirmAlready"
                values={{ productName, listName }}
                components={{
                    product: <strong className="font-bold" />,
                    list: <strong className="font-bold" />,
                }}
            />
        );
    }

    return (
        <Trans
            i18nKey="product:wishlistSheet.confirmSaved"
            values={{ productName, listName }}
            components={{
                product: <strong className="font-bold" />,
                list: <strong className="font-bold" />,
            }}
        />
    );
}

function AddedToCartToastMessage({
    productName,
    messageKey,
    addAllHeaderKey,
    isHeadingTitle,
    t,
    tAccount,
}: {
    productName?: string;
    messageKey: 'addedToCart' | 'updatedCart' | 'addedAllToCartForWishlist' | 'addedAllToCartForRecipe';
    addAllHeaderKey?: AddAllToCartHeaderKey;
    isHeadingTitle?: boolean;
    t: ProductTFunction;
    tAccount: AccountTFunction;
}): ReactElement {
    if (messageKey === 'addedAllToCartForRecipe' && addAllHeaderKey) {
        if (addAllHeaderKey === 'addedOne' || addAllHeaderKey === 'addedMultiple') {
            return (
                <p className="min-w-0 flex-1 break-words body-copy-m text-neutral-10">{t('recipeAddedAllToCart')}</p>
            );
        }

        return (
            <p
                className={cn(
                    'min-w-0 flex-1 text-neutral-10 break-words',
                    isHeadingTitle ? 'heading-title-l' : 'body-copy-m'
                )}>
                {tAccount(`wishlist.addToCartNotification.${addAllHeaderKey}`)}
            </p>
        );
    }

    if (messageKey === 'addedAllToCartForWishlist' && addAllHeaderKey) {
        return (
            <p
                className={cn(
                    'min-w-0 flex-1  text-neutral-10 break-words',
                    isHeadingTitle ? 'heading-title-l ' : 'body-copy-m'
                )}>
                {tAccount(`wishlist.addToCartNotification.${addAllHeaderKey}`)}
            </p>
        );
    }

    return (
        <p className="min-w-0 flex-1 break-words body-copy-m text-neutral-10">
            {messageKey === 'updatedCart' && productName ? (
                <Trans
                    t={t}
                    i18nKey="updatedCart"
                    values={{ productName }}
                    components={{ product: <span className="body-copy-m-bold" /> }}
                />
            ) : messageKey === 'addedToCart' && productName ? (
                <Trans
                    t={t}
                    i18nKey="addedToCart"
                    values={{ productName }}
                    components={{ bold: <span className="body-copy-m-bold" /> }}
                />
            ) : null}
        </p>
    );
}

function AddAllToCartSecondarySection({
    messageKey,
    isTitleAddedNone,
    presentation,
    t,
}: {
    messageKey: AddAllToCartSecondaryKey;
    isTitleAddedNone: boolean;
    presentation: 'toast' | 'drawer';
    t: AccountTFunction;
}): ReactElement {
    return (
        <div className={cn('bg-neutral-white ', presentation === 'toast' ? 'px-6 pb-4' : 'px-0')}>
            <div className={cn(!isTitleAddedNone && 'rounded-[var(--corner-16)] bg-[var(--status-red-95)] px-4 py-3')}>
                <p className="text-sm leading-5 tracking-[0.14px] text-foreground">
                    {t(`wishlist.addToCartNotification.${messageKey}`)}
                </p>
            </div>
        </div>
    );
}

export function RemovedFromCartToastContent({
    productName,
    t,
    messageKey,
    presentation = 'toast',
}: RemovedFromCartToastOptions & { t: ProductTFunction } & CommerceFeedbackContentProps): ReactElement {
    const key = messageKey ?? 'removedFromCart';
    return (
        <div className={cn(commerceFeedbackContentClassName(presentation), 'body-copy-m text-neutral-10')}>
            <Trans
                t={t}
                i18nKey={key}
                values={{ productName }}
                components={{ product: <span className="body-copy-m-bold" /> }}
            />
        </div>
    );
}

export function BasketQuotaExceededToastContent({
    tAccount,
    presentation = 'toast',
}: { tAccount: AccountTFunction } & CommerceFeedbackContentProps): ReactElement {
    return (
        <div className={cn('flex w-full items-start gap-3', commerceFeedbackContentClassName(presentation))}>
            <p className="min-w-0 flex-1 break-words body-copy-m text-neutral-10">
                {tAccount('wishlist.addToCartNotification.titleAddedNone')}
            </p>
        </div>
    );
}

export function FailedToAddToCartToastContent({
    t,
    presentation = 'toast',
}: { t: ProductTFunction } & CommerceFeedbackContentProps): ReactElement {
    return (
        <p className={cn(commerceFeedbackContentClassName(presentation), 'body-copy-m text-neutral-10')}>
            {t('failedToAddToCart')}
        </p>
    );
}

export function FailedToUpdateCartToastContent({
    t,
    presentation = 'toast',
}: { t: ProductTFunction } & CommerceFeedbackContentProps): ReactElement {
    return (
        <p className={cn(commerceFeedbackContentClassName(presentation), 'body-copy-m text-neutral-10')}>
            {t('failedToUpdateCart')}
        </p>
    );
}

export function MinOrderFeedbackToastContent({
    formattedAmount,
    t,
    presentation = 'toast',
}: MinOrderFeedbackToastOptions & { t: CartTFunction } & CommerceFeedbackContentProps): ReactElement {
    return (
        <p className={cn(commerceFeedbackContentClassName(presentation), 'body-copy-m text-neutral-10')}>
            <MinOrderFeedbackMessage formattedAmount={formattedAmount} t={t} />
        </p>
    );
}

/** Shared minimum-order copy for toast and drawer feedback. */
export function MinOrderFeedbackMessage({
    formattedAmount,
    t,
}: MinOrderFeedbackToastOptions & { t: CartTFunction }): ReactElement {
    return (
        <Trans
            t={t}
            i18nKey="checkout.minimumOrderAmount"
            values={{ amount: formattedAmount }}
            components={{ amount: <span className="body-copy-m-bold" /> }}
        />
    );
}

export function AddedToCartToastContent({
    productName,
    approachingPromotions,
    messageKey = 'addedToCart',
    addedItemCount,
    unavailableItemCount = 0,
    isBasketQuotaExceeded,
    presentation = 'toast',
    editorialPromotions,
    t,
    tAccount,
    tCart,
    currency,
    language,
}: AddedToCartToastContentProps): ReactNode {
    const toastPromotions = approachingPromotions ?? [];
    const hasApproachingPromotions = toastPromotions.length > 0;
    const isWishlistAddAll = messageKey === 'addedAllToCartForWishlist';
    const isRecipeAddAll = messageKey === 'addedAllToCartForRecipe';
    const addAllUi =
        isWishlistAddAll || isRecipeAddAll
            ? resolveAddAllToCartToastUi({
                  addedItemCount,
                  unavailableItemCount,
                  isBasketQuotaExceeded,
              })
            : null;
    const isTitleAddedNone = addAllUi?.headerKey === 'titleAddedNone';
    const hasAddAllSecondary = Boolean(addAllUi?.secondaryKey);
    const toastEditorialPromotions = editorialPromotions ?? [];
    const hasEditorialPromotions = toastEditorialPromotions.length > 0;
    const hasSecondarySections = hasApproachingPromotions || hasAddAllSecondary || hasEditorialPromotions;
    const isHeadingTitle = addAllUi?.isHeadingTitle;

    const headerClassName = cn(
        'flex w-full items-start gap-3',
        presentation === 'toast' ? 'py-4 pl-6 pr-16' : 'py-0 pl-0 pr-0',
        hasSecondarySections && !isHeadingTitle && presentation === 'toast' && 'bg-sand-95'
    );
    const showCartIcon = isWishlistAddAll || isRecipeAddAll ? (addAllUi?.showCartIcon ?? false) : true;
    const message = (
        <AddedToCartToastMessage
            productName={productName}
            messageKey={messageKey}
            addAllHeaderKey={addAllUi?.headerKey}
            isHeadingTitle={isHeadingTitle}
            t={t}
            tAccount={tAccount}
        />
    );

    if (!hasSecondarySections) {
        return (
            <div className={headerClassName}>
                {showCartIcon ? (
                    <SvgCartFeedback className="size-6 shrink-0 text-neutral-10" aria-hidden="true" />
                ) : null}
                {message}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'flex w-full flex-col gap-2',
                hasApproachingPromotions && !hasEditorialPromotions && !addAllUi?.secondaryKey && 'pb-4'
            )}>
            <div className={cn(headerClassName, presentation === 'drawer' ? 'pb-4' : 'mb-4')}>
                {showCartIcon ? (
                    <SvgCartFeedback className="size-6 shrink-0 text-neutral-10" aria-hidden="true" />
                ) : null}
                {message}
            </div>
            {hasApproachingPromotions ? (
                <div
                    className={cn(
                        presentation === 'toast' && 'px-6',
                        hasEditorialPromotions && !addAllUi?.secondaryKey && 'pb-4'
                    )}>
                    <ApproachingPromotionsBanner
                        promotions={toastPromotions}
                        currency={currency}
                        t={tCart}
                        language={language}
                        variant="toast"
                    />
                </div>
            ) : null}
            {hasEditorialPromotions ? (
                <div className={cn(presentation === 'toast' && 'px-6 -mx-6 w-auto')}>
                    <CartEditorialPromotions promotions={toastEditorialPromotions} presentation={presentation} />
                </div>
            ) : null}
            {addAllUi?.secondaryKey ? (
                <AddAllToCartSecondarySection
                    messageKey={addAllUi.secondaryKey}
                    isTitleAddedNone={isTitleAddedNone}
                    presentation={presentation}
                    t={tAccount}
                />
            ) : null}
        </div>
    );
}
